import { IAssetFiles } from '../interfaces/assetManifest';

const MODULE_LOADER_SELECTOR = '.module-loader';
const HIDE_CLASS = 'hide';
const LOADING_TIMEOUT = 10 * 1000;
let errorDisplayed = false;

const loadManifest = (moduleName: string): Promise<IAssetFiles> => {
  const now = new Date();
  // breaking cache every minute for manifest files
  const timestamp = [now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes()].join('-');

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      let manifest = typeof xhr.response === 'string' ? JSON.parse(xhr.response) : xhr.response; // parse because of IE
      if (manifest && manifest.files) {
        // modules built with cdk-react-scripts have IAssetManifest instead of IAssetFiles
        manifest = manifest.files;
      }
      resolve(manifest);
    };
    xhr.onerror = () => reject(`Failed to load manifest for ${moduleName}.`);
    xhr.ontimeout = () => reject(`Loading manifest for ${moduleName} timed out.`);
    xhr.onabort = () => reject(`Loading manifest for ${moduleName} was aborted.`);
    xhr.open('GET', `/static/${moduleName}/asset-manifest.json?timestamp=${timestamp}`, true);
    xhr.responseType = 'json';
    xhr.send();
  });
};

const displayModuleError = (moduleWrapper: Element): void => {
  // hide loading spinner
  const spinner = moduleWrapper.querySelector('.module-loader-spinner');

  if (spinner) {
    spinner.classList.add(HIDE_CLASS);
  }

  // display error (but only once for current page)
  if (!errorDisplayed) {
    const errorContainer = moduleWrapper.querySelector('.loading-error');
    if (errorContainer) {
      errorContainer.classList.remove(HIDE_CLASS);
      errorDisplayed = true;
    }
  }
};

const displayErrorAfterTimeout = (moduleWrapper: Element): void => {
  setTimeout(() => displayModuleError(moduleWrapper), LOADING_TIMEOUT);
};

function buildResourceUrl(moduleName: string, path: string) {
  return ['/static/', moduleName, path.charAt(0) === '/' ? '' : '/', path].join('');
}

const loadModuleBundle = async (moduleWrapper: Element, moduleName: string, loadCss: boolean): Promise<void> => {
  const manifest = await loadManifest(moduleName);
  const scriptElement = document.createElement('script');
  scriptElement.async = true;
  scriptElement.src = buildResourceUrl(moduleName, manifest['main.js']);
  document.body.appendChild(scriptElement);

  if (loadCss && manifest['main.css']) {
    const cssElement = document.createElement('link');
    cssElement.rel = 'stylesheet';
    cssElement.href = buildResourceUrl(moduleName, manifest['main.css']);
    document.head.appendChild(cssElement);
  }
};

export const initLoadPresentModules = (): Promise<any> => {
  const requestedModules: { [key: string]: boolean } = {};
  const loaders: Promise<any>[] = [];

  const moduleWrappers: NodeListOf<Element> = document.querySelectorAll(MODULE_LOADER_SELECTOR);

  for (let i = 0; i < moduleWrappers.length; i++) {
    const moduleWrapper: Element = moduleWrappers.item(i);
    try {
      const moduleName = moduleWrapper.getAttribute('data-module');

      // admin has not specified module yet.
      if (!moduleName) {
        return;
      }

      const loadCss = moduleWrapper.getAttribute('data-styling');

      moduleWrapper.classList.remove(HIDE_CLASS);

      if (!requestedModules[moduleName]) {
        requestedModules[moduleName] = true;
        loaders.push(loadModuleBundle(moduleWrapper, moduleName, loadCss === 'true'));
      }
      displayErrorAfterTimeout(moduleWrapper);
    } catch (err) {
      displayModuleError(moduleWrapper);
    }
  }
  return Promise.all(loaders);
};
